/**
 * DXF (AutoCAD) inlezen voor de projectkaart.
 *
 * Twee dingen uit één tekening:
 *   1. een ACHTERGRONDLAAG: lijnen, polylijnen, cirkels, bogen en teksten → GeoJSON
 *   2. KANDIDATEN VOOR BOORPUNTEN: per blok (INSERT) of laag een groep punten met een naam.
 *      Een boorplan uit CAD tekent een boorpunt bijna altijd als blok met een attribuut 'NR',
 *      of als cirkel/punt met een losse tekst ernaast. Welke groep de boorpunten zijn weet
 *      alleen de gebruiker; de importdialoog laat hem kiezen.
 *
 * Eigen lezer i.p.v. een bibliotheek: we hebben maar een handvol entiteiten nodig, en het
 * uitvouwen van blokken (met schaal en rotatie) moesten we toch zelf doen.
 *
 * Coördinaten blijven in de eenheid van de tekening; omrekenen naar WGS84 doet de aanroeper
 * (meestal RD in meters; soms millimeters, zie `guessDxfScale`).
 */

/** DXF-tekst → lijst [code, waarde]-paren. */
function pairs(text) {
  const lines = text.split(/\r?\n/);
  const out = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = parseInt(lines[i].trim(), 10);
    if (Number.isNaN(code)) { i -= 1; continue; } // scheef geraakt: één regel opschuiven
    out.push([code, lines[i + 1]]);
  }
  return out;
}

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/** MTEXT-opmaakcodes eruit: {\fArial|b0;B01} → B01, \P → spatie. */
export function cleanMText(s) {
  return String(s ?? '')
    .replace(/\\P/g, ' ')
    .replace(/\\[A-Za-z][^;\\{}]*;/g, '')
    .replace(/\\[~ ]/g, ' ')
    .replace(/[{}]/g, '')
    .replace(/%%[cC]/g, 'Ø').replace(/%%[dD]/g, '°').replace(/%%[pP]/g, '±')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Entiteiten uit een reeks paren lezen, vanaf index i tot een eindmarkering.
 * Geeft de entiteiten en de index waar gestopt is.
 */
function readEntities(p, i, stopAt) {
  const entities = [];
  let cur = null;
  let vertexTarget = null; // POLYLINE die VERTEX-en verzamelt
  let insertTarget = null; // INSERT die ATTRIB-en verzamelt
  const finish = () => {
    if (!cur) return;
    if (cur.type === 'VERTEX' && vertexTarget) vertexTarget.points.push([cur.x, cur.y]);
    else if (cur.type === 'ATTRIB' && insertTarget) insertTarget.attribs.push({ tag: cur.tag, value: cleanMText(cur.text) });
    else if (cur.type === 'SEQEND') { vertexTarget = null; insertTarget = null; }
    else if (cur.type !== 'VERTEX' && cur.type !== 'ATTRIB') {
      entities.push(cur);
      if (cur.type === 'POLYLINE') vertexTarget = cur;
      if (cur.type === 'INSERT' && cur.hasAttribs) insertTarget = cur;
    }
    cur = null;
  };
  for (; i < p.length; i++) {
    const [code, raw] = p[i];
    const val = raw.trim();
    if (code === 0) {
      finish();
      if (stopAt.includes(val)) return { entities, i };
      cur = { type: val, layer: '0', x: 0, y: 0, points: [], attribs: [], text: '' };
      continue;
    }
    if (!cur) continue;
    switch (code) {
      case 8: cur.layer = val; break;
      case 2: cur.block = val; if (cur.type === 'ATTRIB') cur.tag = val; break;
      // MTEXT: lange tekst komt in stukken van code 3, het laatste stuk in code 1.
      case 1: cur.text = (cur.pre || '') + raw; break;
      case 3: cur.pre = (cur.pre || '') + raw; break;
      case 10:
        if (cur.type === 'LWPOLYLINE') cur.points.push([num(val), 0]);
        else cur.x = num(val);
        break;
      case 20:
        if (cur.type === 'LWPOLYLINE' && cur.points.length) cur.points[cur.points.length - 1][1] = num(val);
        else cur.y = num(val);
        break;
      case 11: cur.x2 = num(val); break;
      case 21: cur.y2 = num(val); break;
      case 40: cur.r = num(val); break;
      case 41: cur.sx = num(val); break;
      case 42: cur.sy = num(val); break;
      case 50: cur.a1 = num(val); break;
      case 51: cur.a2 = num(val); break;
      case 66: cur.hasAttribs = num(val) === 1; break;
      case 70: cur.flags = num(val); break;
      default: break;
    }
  }
  finish();
  return { entities, i };
}

/**
 * @param {string} text  inhoud van een ASCII-DXF
 * @returns {{ entities: object[], blocks: Record<string,{base:[number,number], entities: object[]}>, insUnits: number|null }}
 */
export function parseDxf(text) {
  if (/^AutoCAD Binary DXF/.test(text.slice(0, 22))) {
    throw new Error('Binaire DXF wordt niet ondersteund; sla de tekening op als ASCII-DXF.');
  }
  const p = pairs(text);
  const blocks = {};
  let entities = [];
  let insUnits = null;
  for (let i = 0; i < p.length; i++) {
    const [code, raw] = p[i];
    if (code === 9 && raw.trim() === '$INSUNITS' && p[i + 1]) insUnits = num(p[i + 1][1]);
    if (code !== 0 || raw.trim() !== 'SECTION') continue;
    const name = p[i + 1]?.[1]?.trim();
    if (name === 'ENTITIES') {
      const r = readEntities(p, i + 2, ['ENDSEC']);
      entities = r.entities; i = r.i;
    } else if (name === 'BLOCKS') {
      i += 2;
      while (i < p.length && !(p[i][0] === 0 && p[i][1].trim() === 'ENDSEC')) {
        if (p[i][0] === 0 && p[i][1].trim() === 'BLOCK') {
          let bname = '', bx = 0, by = 0;
          let j = i + 1;
          for (; j < p.length && p[j][0] !== 0; j++) {
            if (p[j][0] === 2) bname = p[j][1].trim();
            if (p[j][0] === 10) bx = num(p[j][1]);
            if (p[j][0] === 20) by = num(p[j][1]);
          }
          const r = readEntities(p, j, ['ENDBLK']);
          blocks[bname] = { base: [bx, by], entities: r.entities };
          i = r.i;
        }
        i++;
      }
    }
  }
  return { entities, blocks, insUnits };
}

// ── Uitvouwen naar wereldcoördinaten ─────────────────────────────────────────

const DEG = Math.PI / 180;

function compose(t, ins, base) {
  // Lokale transformatie van een INSERT: verschuif basispunt, schaal, roteer, verplaats.
  const sx = ins.sx || 1, sy = ins.sy || ins.sx || 1;
  const a = (ins.a1 || 0) * DEG;
  const c = Math.cos(a), s = Math.sin(a);
  const local = ([x, y]) => {
    const lx = (x - base[0]) * sx, ly = (y - base[1]) * sy;
    return [ins.x + lx * c - ly * s, ins.y + lx * s + ly * c];
  };
  return (pt) => t(local(pt));
}

function arcPoints(cx, cy, r, a1, a2) {
  let end = a2;
  while (end <= a1) end += 360;
  const steps = Math.max(8, Math.ceil((end - a1) / 10));
  const out = [];
  for (let k = 0; k <= steps; k++) {
    const a = (a1 + ((end - a1) * k) / steps) * DEG;
    out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return out;
}

/**
 * Alle entiteiten plat, in tekeningcoördinaten. INSERT-en worden uitgevouwen (max. 4 diep,
 * een blok dat zichzelf bevat zou anders eindeloos doorgaan). Elke INSERT levert daarnaast een
 * 'insert'-item met zijn invoegpunt en attributen, voor de boorpuntkandidaten.
 *
 * @returns {{ lines: {layer:string, coords:number[][], closed:boolean}[], points: {layer:string, x:number, y:number, source:string, name:string|null, r?:number}[], texts: {layer:string, x:number, y:number, text:string}[] }}
 */
export function flattenDxf(dxf) {
  const lines = [], points = [], texts = [];
  const walk = (ents, t, depth, layerOverride) => {
    for (const e of ents) {
      const layer = e.layer === '0' && layerOverride ? layerOverride : e.layer;
      switch (e.type) {
        case 'LINE':
          lines.push({ layer, coords: [t([e.x, e.y]), t([e.x2 ?? e.x, e.y2 ?? e.y])], closed: false });
          break;
        case 'LWPOLYLINE':
        case 'POLYLINE':
          if (e.points.length >= 2) {
            const closed = ((e.flags || 0) & 1) === 1;
            const coords = e.points.map(t);
            if (closed) coords.push(coords[0]);
            lines.push({ layer, coords, closed });
          }
          break;
        case 'CIRCLE': {
          const c = t([e.x, e.y]);
          lines.push({ layer, coords: arcPoints(e.x, e.y, e.r || 0, 0, 360).map(t), closed: true, circle: true });
          // Een kleine cirkel is in een boorplan vaak het boorpunt zelf.
          points.push({ layer, x: c[0], y: c[1], source: `cirkel · laag ${layer}`, name: null, r: e.r || 0 });
          break;
        }
        case 'ARC':
          lines.push({ layer, coords: arcPoints(e.x, e.y, e.r || 0, e.a1 || 0, e.a2 || 360).map(t), closed: false });
          break;
        case 'POINT': {
          const c = t([e.x, e.y]);
          points.push({ layer, x: c[0], y: c[1], source: `punt · laag ${layer}`, name: null });
          break;
        }
        case 'TEXT':
        case 'MTEXT': {
          const text = cleanMText(e.text);
          if (text) { const c = t([e.x, e.y]); texts.push({ layer, x: c[0], y: c[1], text }); }
          break;
        }
        case 'INSERT': {
          const block = dxf.blocks[e.block];
          const c = t([e.x, e.y]);
          const named = e.attribs.find(a => /^(nr|no|num|nummer|naam|name|id|code|punt|boring|label)/i.test(a.tag) && a.value)
            || e.attribs.find(a => a.value);
          points.push({ layer, x: c[0], y: c[1], source: `blok ${e.block}`, name: named?.value || null, insert: true });
          for (const a of e.attribs) if (a.value) texts.push({ layer, x: c[0], y: c[1], text: a.value, attrib: true });
          if (block && depth < 4) walk(block.entities, compose(t, e, block.base), depth + 1, layer);
          break;
        }
        default: break;
      }
    }
  };
  walk(dxf.entities, (pt) => pt, 0, null);
  return { lines, points, texts };
}

/**
 * Kandidaatgroepen voor boorpunten. Per bron (blok X, cirkels op laag Y, punten op laag Z) de
 * punten met een naam: uit een attribuut, of anders de dichtstbijzijnde losse tekst binnen
 * `maxTextDistance` (tekeningeenheden). Gesorteerd: groepen met namen eerst.
 */
export function boreholeCandidates(flat, { maxTextDistance = 5 } = {}) {
  const groups = new Map();
  for (const p of flat.points) {
    // Grote cirkels zijn contouren (tank, put), geen boorpunt.
    if (p.r != null && p.r > 2.5) continue;
    if (!groups.has(p.source)) groups.set(p.source, []);
    groups.get(p.source).push({ ...p });
  }
  const freeTexts = flat.texts.filter(t => !t.attrib);
  const out = [];
  for (const [source, pts] of groups) {
    for (const p of pts) {
      if (p.name) continue;
      let best = null, bestD = maxTextDistance;
      for (const t of freeTexts) {
        const d = Math.hypot(t.x - p.x, t.y - p.y);
        if (d < bestD) { best = t; bestD = d; }
      }
      if (best) p.name = best.text;
    }
    // Hetzelfde blok twee keer op dezelfde plek (tekenfout of geneste kopie) telt één keer.
    const seen = new Set();
    const unique = pts.filter(p => {
      const k = `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
    const named = unique.filter(p => p.name).length;
    out.push({ source, count: unique.length, named, points: unique });
  }
  return out
    .filter(g => g.count > 0)
    .sort((a, b) => (b.named / b.count) - (a.named / a.count) || b.count - a.count);
}

/**
 * Schaal van de tekening naar meters.
 *
 * Eerst de coördinaten zelf: vallen ze zoals ze zijn in RD, dan is het meters; vallen ze pas in
 * RD na delen door 1000 (of 100), dan millimeters (of centimeters). Pas als dat niets oplevert
 * telt $INSUNITS (4 = mm, 5 = cm, 6 = m). Die volgorde is bewust: het standaardsjabloon van
 * AutoCAD (acadiso) zet INSUNITS op millimeters, ook als er in RD-meters getekend is -- en dan
 * zou de tekening duizend keer te klein op de kaart komen.
 */
export function guessDxfScale(dxf, samplePts, guessSystem) {
  if (samplePts.length) {
    for (const scale of [1, 0.001, 0.01]) {
      const g = guessSystem(scale === 1 ? samplePts : samplePts.map(([x, y]) => [x * scale, y * scale]));
      if (g && g.confidence > 0.8) return scale;
    }
  }
  if (dxf.insUnits === 4) return 0.001;
  if (dxf.insUnits === 5) return 0.01;
  return 1;
}

/**
 * Plat gemaakte tekening → GeoJSON FeatureCollection in WGS84. `toWgs84([x,y])` geeft
 * [lon, lat] of null. Teksten gaan mee als punten met een `text`-eigenschap (de kaart toont ze
 * als label); losse cirkels/punten niet -- die zitten al in de lijnen of worden geplande punten.
 */
export function dxfToGeoJSON(flat, toWgs84, { includeTexts = true, layers = null } = {}) {
  const features = [];
  const keep = (l) => !layers || layers.has(l);
  for (const l of flat.lines) {
    if (!keep(l.layer)) continue;
    const coords = l.coords.map(toWgs84);
    if (coords.some(c => !c)) continue;
    features.push({ type: 'Feature', properties: { layer: l.layer }, geometry: { type: 'LineString', coordinates: coords } });
  }
  if (includeTexts) {
    for (const t of flat.texts) {
      if (t.attrib || !keep(t.layer)) continue;
      const c = toWgs84([t.x, t.y]);
      if (c) features.push({ type: 'Feature', properties: { layer: t.layer, text: t.text }, geometry: { type: 'Point', coordinates: c } });
    }
  }
  return { type: 'FeatureCollection', features };
}

/** Alle laagnamen met het aantal lijnen erop, voor de laagkeuze in de dialoog. */
export function dxfLayerSummary(flat) {
  const m = new Map();
  for (const l of flat.lines) m.set(l.layer, (m.get(l.layer) || 0) + 1);
  for (const t of flat.texts) if (!t.attrib) m.set(t.layer, (m.get(t.layer) || 0) + 1);
  return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
}
